import styled, { css, useTheme } from "styled-components";
import Text from "common/Typography/Text";
import { formatDate } from "@shared/utils/date";
import { CSSProperties, ReactNode } from "react";
import { typography } from "@shared/common/Themes/typography";
import { TITLE_TO_VALUE_TO_HELPER_TOOLTIP } from "@shared/features/trades/common/constants";
import {
  HelperTooltip,
  HelperTooltipProps,
} from "common/Tooltips/HelperTooltip";
import Flex from "common/Boxes/Flex";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface ExtraInfoItemProps {
  name: string;
  value: any;
  bordered?: boolean;
  height?: number;
  autoHeight?: boolean;
  valueFlex?: string;
  style?: CSSProperties;
  titleHelperProps?: HelperTooltipProps;
  valueTextStyle?: CSSProperties;
  valueWrapperStyle?: CSSProperties;
  nameWrapperStyle?: CSSProperties;
  ellipsis?: boolean;
}

const ExtraInfoItem = ({
  name,
  value,
  bordered = true,
  height = 50,
  autoHeight,
  valueFlex,
  style,
  titleHelperProps,
  valueTextStyle,
  valueWrapperStyle,
  nameWrapperStyle,
  ellipsis,
}: ExtraInfoItemProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const appTheme = useTheme();
  let parsedValue: ReactNode;
  if (DATE_HEADERS.indexOf(name) >= 0) {
    parsedValue = formatDate(value, true, true);
  } else {
    parsedValue = value;
  }

  const getParsedValue = () => {
    return parsedValue ?? "N/A";
  };

  const helperTooltipData = TITLE_TO_VALUE_TO_HELPER_TOOLTIP?.[name]?.[value];
  const helperTooltipProps = {
    ...helperTooltipData,
    description: t(helperTooltipData?.description),
    title: t(helperTooltipData?.title),
  };

  return (
    <ItemWrapper
      $bordered={bordered}
      $height={height}
      $autoHeight={autoHeight}
      style={style}
    >
      <Text
        as="div"
        type="secondary"
        style={{ paddingRight: 8, ...nameWrapperStyle }}
      >
        {name}
        {titleHelperProps && <HelperTooltip {...titleHelperProps} />}
      </Text>
      <Flex
        $alignItems="flex-start"
        style={{
          flex: valueFlex,
          width: "100%",
          overflow: "hidden",
          ...valueWrapperStyle,
        }}
      >
        {helperTooltipProps && (
          <HelperTooltip
            iconStyle={{
              marginRight: 6,
              color: appTheme.colors.primaryGrey,
            }}
            {...helperTooltipProps}
          />
        )}
        <Text
          $weight={typography.fontWeights.MEDIUM}
          as="div"
          $ellipsis={ellipsis}
          style={{ width: "100%", ...valueTextStyle }}
        >
          {getParsedValue()}
        </Text>
      </Flex>
    </ItemWrapper>
  );
};

export default ExtraInfoItem;

const DATE_HEADERS = [
  "Opened at",
  "Creation Date",
  "Closed at",
  "Close Time",
  "Open Time",
];

const ItemWrapper = styled.div<{
  $bordered: boolean;
  $height: number;
  $autoHeight?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${(p) => p.$height}px;
  ${(p) =>
    p.$bordered &&
    `
    &:not(:last-of-type) {
      border-bottom: ${p.theme.borders.lightest};
    }
  `}
  ${(p) =>
    p.$autoHeight &&
    `
    height: auto;
    min-height: ${p.$height}px;
    padding: 8px 0;
  `}
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A row that closes its section (a heading follows) needs no line;
         the section's own spacing separates it. */
      &&:has(+ h2),
      &&:has(+ h3),
      &&:has(+ h4),
      &&:has(+ h5) {
        border-bottom: none;
      }
      /* Rows that can grow: the label sits on the first line of the value
         instead of floating in the middle of a tall row. A single line keeps
         the row height. */
      ${p.$autoHeight &&
      css`
        align-items: flex-start;
        padding: ${(p.$height - ROW_LINE_HEIGHT) / 2}px 0;
      `}
    `}
`;

/** Line height of the label and value text. */
const ROW_LINE_HEIGHT = 22;
